import React, { useEffect, useState } from "react";
import { TradeInAemLabel } from "../../../utils/labels";
import { type TradeInAemDispatchType, useTradeInAemSelector } from "../../../redux/store";
import { getInternalLink } from "../../../utils/navUtils";
import FindVehicleDropdown from "./FindVehicleDropdown";
import * as Styles from "./styles/Vehicle";
import Icon, { IconExclamation } from "../../../../common-deprecated/styles/v2/toyota/globals/Icon";
import { IconWrapper } from "../../../../common-deprecated/styles/v2/toyota/globals/IconWrapper";
import * as Button from "../../../../common-deprecated/styles/v2/toyota/components/Button";
import * as Text from "../../../../common-deprecated/styles/v2/toyota/utilities/Text";
import { isRTL } from "../../../../common-deprecated/settings/utils/commonSettingUtils";
import { useTradeInCsl } from "../../../csl/tradeInCslUtils";
import { trackStartSearch } from "../../../utils/tracking";
import usePreviouslySearchedVehicles from "../../../hooks/usePreviouslySearchedVehicles";
import Preloader from "../../../../common-deprecated/styles/v2/toyota/custom/Preloader";
import useTrackingParams from "../../../hooks/useTrackingParams";
import { useDispatch } from "../../../../shared-logic/redux/store";
import * as Form from "../common/styles/FormsAem";
import FindVehicleResolveInput from "./FindVehicleResolveInput";
import { TradeInAemFindVehicleStatus, TradeInAemResolveItemType } from "../../../utils/types";

const FindVehicleResolve = (): JSX.Element => {
    const dispatch = useDispatch<TradeInAemDispatchType>();
    const currentStep = useTradeInAemSelector((state) => state.tradeInAemNav.step);
    const commonSettings = useTradeInAemSelector((state) => state.commonSettings);
    const resolveForm = useTradeInAemSelector((state) => state.tradeInAemFindVehicle.resolveForm);
    const resolveVehicle = useTradeInAemSelector((state) => state.tradeInAemFindVehicle.resolveVehicle);
    const loading = useTradeInAemSelector((state) => state.tradeInAemFindVehicle.resolveLoading);
    const vehicle = useTradeInAemSelector((state) => state.tradeInAemVehicle.vehicle);
    const failed = useTradeInAemSelector((state) => state.tradeInAemFindVehicle.resolveFailed);
    const hasSearchResult = useTradeInAemSelector((state) => !!state.tradeInAemFindVehicle.searchResults.length);
    const searchInsufficient = useTradeInAemSelector(
        (state) => state.tradeInAemFindVehicle.searchStatus === TradeInAemFindVehicleStatus.Insufficient,
    );
    const csl = useTradeInCsl();
    const { addToPreviouslySearched } = usePreviouslySearchedVehicles();
    const trackingParams = useTrackingParams();
    const nextStep = getInternalLink(csl, currentStep, dispatch, commonSettings, "next");
    const isResolveOnly = !csl.enableLicensePlate && !csl.enableVin && csl.enableResolve;

    const [open, setOpen] = useState(searchInsufficient || isResolveOnly);
    const toggle = (): void => {
        if (!open) trackStartSearch(trackingParams, "select");
        setOpen(!open);
    };

    // When the dropdown is opened and we don't have form info, initiate the first fetch.
    useEffect(() => {
        if (!resolveForm.length && open) csl.fetchResolveVehicle();
    }, [open]);

    // When a vehicle is found via search, we want to disable all select / input fields. Once a match is found via search, no resolving should be possible.
    // Also set the fields as disabled when resolve is loading so the user can't modify their choices during a fetch. This could result in wrong end results.
    const isFieldDisabled = hasSearchResult || loading;
    // All input fields should be filled in before select dropdowns can become interactible. This is required as no fetch is started when an input fields updates.
    const hasEmptyInputFields = resolveForm.some(
        (form) =>
            form.type === TradeInAemResolveItemType.Input &&
            (!resolveVehicle[form.vehicleProperty] || resolveVehicle[form.vehicleProperty].input === ""),
    );

    return (
        <div>
            {isResolveOnly && <TradeInAemLabel label="tradeInAemFindVehicleUseOptionsToFindYourCar" />}
            {!searchInsufficient && !isResolveOnly && (
                <Styles.FindVehicleLink onClick={toggle} isOpen={open}>
                    <TradeInAemLabel label="tradeInAemFindVehicleCannotFindMyCar" />
                    <IconWrapper size="xxs">
                        <Icon variant={isRTL(commonSettings) ? "chevronLeft" : "chevronRight"} />
                    </IconWrapper>
                </Styles.FindVehicleLink>
            )}
            {open && (
                <Styles.FindVehicleForm>
                    {resolveForm.map((formItem) =>
                        formItem.type === TradeInAemResolveItemType.Input ? (
                            <FindVehicleResolveInput
                                key={formItem.vehicleProperty}
                                formItem={formItem}
                                disabled={isFieldDisabled}
                            />
                        ) : (
                            <FindVehicleDropdown
                                key={formItem.vehicleProperty}
                                dropdownConfig={formItem}
                                onOptionSelect={csl.onResolveOptionSelect}
                                disabled={isFieldDisabled || hasEmptyInputFields} // As long as there are empty input fields, the resolve dropdowns should be disabled.
                            />
                        ),
                    )}
                    {loading && (
                        <Styles.LoadingWrapper>
                            <Preloader size={20} />
                            <TradeInAemLabel label="tradeInAemLoading" />
                        </Styles.LoadingWrapper>
                    )}

                    {failed && !loading && (
                        <Form.Error>
                            <IconWrapper iconColor="primaryRed">
                                <IconExclamation />
                            </IconWrapper>
                            <TradeInAemLabel label="tradeInAemResolveFailed" />
                        </Form.Error>
                    )}

                    {/* 
                    If resolve is shown because the previous search was insufficient and a vehicle is finally found,
                    we don't want to show the confirm CTA. Instead show the search results component. 
                    */}
                    {!failed && vehicle && !searchInsufficient && (
                        <Styles.FindVehicleBtnHolder>
                            <Button.Primary
                                m={0}
                                as="a"
                                href={nextStep.href}
                                onClick={(event: React.SyntheticEvent) => {
                                    if (nextStep.onClick) nextStep.onClick(event);
                                    addToPreviouslySearched(vehicle);
                                }}
                            >
                                <Text.Button>
                                    <TradeInAemLabel label="tradeInAemConfirm" />
                                </Text.Button>
                            </Button.Primary>
                        </Styles.FindVehicleBtnHolder>
                    )}
                </Styles.FindVehicleForm>
            )}
        </div>
    );
};
export default FindVehicleResolve;
